import { DashboardApiFilters, fetchDashboardEndpoint } from "../dashboardApi";

export type SalesUserRole = "normal" | "team_manager";

export type ActivityPeriod = "by_week" | "by_month" | "by_year";

export type RecordKind = "leads" | "pipelines" | "deals";

export interface EntityTotal {
  count: number;
  amount: number;
}

export interface SalesUserScorecards {
  company: EntityTotal;
  contact: EntityTotal;
  lead: EntityTotal;
  pipeline: EntityTotal;
  deal: EntityTotal;
  wonDeals: EntityTotal;
  lostDeals: EntityTotal;
  openDeals: EntityTotal;
  totalSales: number;
  totalRevenue: number;
  totalActivities: number;
  totalSalesGrowth: string;
  totalRevenueGrowth: string;
  totalActivitiesGrowth: string;
}

export interface TeamPerformance {
  teamId: number | null;
  teamName: string;
  members: number;
  managerName: string;
  revenue: number;
  sales: number;
  wonDeals: number;
  lostDeals: number;
  openDeals: number;
}

export interface TeamEntityBreakdown {
  teamId: number | null;
  teamName: string;
  entities: { entity: string; count: number; amount: number }[];
}

export interface RecentRecord {
  id: number | null;
  name: string;
  amount: number;
  stage: string;
  status: string;
  source: string;
  createdAt: string;
}

export interface RecentTask {
  id: number | null;
  name: string;
  description: string;
  status: string;
  createdAt: string;
  dueDate: string;
  assignedTo: string;
  assignedBy: string;
}

export interface StageStat {
  stageId: number | null;
  stageName: string;
  count: number;
  amount: number;
}

export interface ActivityPoint {
  label: string;
  leadCount: number;
  pipelineCount: number;
  dealCount: number;
  leadAmount: number;
  pipelineAmount: number;
  dealAmount: number;
}

export interface SalesUserStats {
  scorecards: SalesUserScorecards;
  teams: TeamPerformance[];
  teamEntities: TeamEntityBreakdown[];
  recent: Record<RecordKind, RecentRecord[]>;
  tasks: RecentTask[];
  stages: Record<RecordKind, StageStat[]>;
  activity: Record<ActivityPeriod, ActivityPoint[]>;
}

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toOptionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toText = (value: unknown) => (value === null || value === undefined ? "" : String(value));

const asArray = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const toTotal = (raw: any): EntityTotal => ({ count: toNumber(raw?.count), amount: toNumber(raw?.total_amount) });

const normalizeRecord = (prefix: "lead" | "pipeline" | "deal") => (item: any): RecentRecord => ({
  id: toOptionalNumber(item?.[`${prefix}_id`]),
  name: toText(item?.[`${prefix}_name`]),
  amount: toNumber(item?.[`${prefix}_amount`]),
  stage: toText(item?.[`${prefix}_stage`]),
  status: toText(item?.[`${prefix}_status`]),
  source: toText(item?.[`${prefix}_source`]),
  createdAt: toText(item?.[`${prefix}_created_at`]),
});

const normalizeStage = (item: any): StageStat => ({
  stageId: toOptionalNumber(item?.stage_id),
  stageName: toText(item?.stage_name),
  count: toNumber(item?.count),
  amount: toNumber(item?.total_amount),
});

const PERIOD_FIELDS: Record<ActivityPeriod, string> = { by_week: "week", by_month: "month", by_year: "year" };

const normalizeActivity = (raw: any, period: ActivityPeriod): ActivityPoint[] =>
  asArray(raw?.[period]).map((item) => ({
    label: toText(item?.[PERIOD_FIELDS[period]] ?? item?.date),
    leadCount: toNumber(item?.lead_count),
    pipelineCount: toNumber(item?.pipeline_count),
    dealCount: toNumber(item?.deal_count),
    leadAmount: toNumber(item?.lead_amount),
    pipelineAmount: toNumber(item?.pipeline_amount),
    dealAmount: toNumber(item?.deal_amount),
  }));

export const normalizeSalesUserStats = (raw: any): SalesUserStats => {
  const cards = raw?.scorecards ?? {};
  const teams = asArray(raw?.total_sales_and_revenue_by_team).map((team) => ({
    teamId: toOptionalNumber(team?.team_id),
    teamName: toText(team?.team_name),
    members: toNumber(team?.team_members),
    managerName: toText(team?.team_manager_name),
    revenue: toNumber(team?.team_revenue),
    sales: toNumber(team?.team_sales),
    wonDeals: toNumber(team?.team_won_deals),
    lostDeals: toNumber(team?.team_lost_deals),
    openDeals: toNumber(team?.team_open_deals),
  }));

  return {
    scorecards: {
      company: toTotal(cards.company),
      contact: toTotal(cards.contact),
      lead: toTotal(cards.lead),
      pipeline: toTotal(cards.pipeline),
      deal: toTotal(cards.deal),
      wonDeals: toTotal(cards.won_deals),
      lostDeals: toTotal(cards.lost_deals),
      openDeals: toTotal(cards.open_deals),
      totalSales: toNumber(cards.total_sales),
      totalRevenue: toNumber(cards.total_revenue),
      totalActivities: toNumber(cards.total_activities),
      totalSalesGrowth: toText(cards.total_sales_growth_rate_by_last_period),
      totalRevenueGrowth: toText(cards.total_revenue_growth_rate_by_last_period),
      totalActivitiesGrowth: toText(cards.total_activities_growth_rate_by_last_period),
    },
    teams,
    teamEntities: asArray(raw?.total_count_and_amount_by_team_and_entities).map((team) => ({
      teamId: toOptionalNumber(team?.team_id),
      teamName: toText(team?.team_name),
      entities: asArray(team?.entities).map((entity) => ({
        entity: toText(entity?.entity_name),
        count: toNumber(entity?.count),
        amount: toNumber(entity?.total_amount),
      })),
    })),
    recent: {
      leads: asArray(raw?.top_recent_leads).map(normalizeRecord("lead")),
      pipelines: asArray(raw?.top_recent_pipelines).map(normalizeRecord("pipeline")),
      deals: asArray(raw?.top_recent_deals).map(normalizeRecord("deal")),
    },
    tasks: asArray(raw?.most_recent_tasks).map((task) => ({
      id: toOptionalNumber(task?.task_id),
      name: toText(task?.task_name),
      description: toText(task?.task_description),
      status: toText(task?.task_status),
      createdAt: toText(task?.task_created_at),
      dueDate: toText(task?.task_due_date),
      assignedTo: toText(task?.task_assigned_to),
      assignedBy: toText(task?.task_assigned_by),
    })),
    stages: {
      leads: asArray(raw?.leads_by_stage).map(normalizeStage),
      pipelines: asArray(raw?.pipelines_by_stage).map(normalizeStage),
      deals: asArray(raw?.deals_by_stage).map(normalizeStage),
    },
    activity: {
      by_week: normalizeActivity(raw?.lead_and_pipeline_and_deal_count_and_amount_by_date, "by_week"),
      by_month: normalizeActivity(raw?.lead_and_pipeline_and_deal_count_and_amount_by_date, "by_month"),
      by_year: normalizeActivity(raw?.lead_and_pipeline_and_deal_count_and_amount_by_date, "by_year"),
    },
  };
};

export const fetchSalesUserStats = async (tenantId: string | number, filters: DashboardApiFilters) =>
  normalizeSalesUserStats(
    await fetchDashboardEndpoint("/dashboard/sales-user-stats/", tenantId, filters, "sales user dashboard")
  );
